import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { registerStudent } from "../services/userService";
import { STUDENT_LEVELS } from "../utils";
import { Spinner } from "react-bootstrap";
import { toast } from 'react-toastify';
import { 
  User, 
  Mail, 
  Phone, 
  Upload, 
  Rocket, 
  CheckCircle, 
  Code,
  Palette,
  Monitor,
  Star,
  Users,
  Calendar,
  Award
} from "lucide-react";

function Registration() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    resume: null,
    studentLevel: STUDENT_LEVELS.FREE_TRAINEE,
  });

  // toast notifications will be used instead of inline alerts
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (e) => {
    setFormData({ ...formData, resume: e.target.files[0] });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFormData({ ...formData, resume: e.dataTransfer.files[0] });
    }
  };

  const realPhone = (phone) => {
    // Basic validation for Indian phone numbers
    const phonePattern = /^[6-9]\d{9}$/;
    return !phonePattern.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Trim all input values
      const trimmedName = formData.name.trim();
      const trimmedEmail = formData.email.trim();
      const trimmedPhone = formData.phone.trim();

      if (!formData.resume) {
        toast.error("Please upload your resume.", { autoClose: 3000 });
        return;
      } else if (formData.resume.size > 5 * 1024 * 1024) {
        toast.error("Resume size should be less than 5MB.", { autoClose: 3000 });
        return;
      } else if (
        ![
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ].includes(formData.resume.type)
      ) {
        toast.error(
          "Invalid resume format. Please upload a PDF or Word document.",
          { autoClose: 3000 }
        );
        return;
      } else if (!/^\d{10}$/.test(trimmedPhone)) {
        toast.error("Please enter a valid 10-digit mobile number.", { autoClose: 3000 });
        return;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        toast.error("Please enter a valid email address.", { autoClose: 3000 });
        return;
      } else if (trimmedName.length < 3) {
        toast.error("Name should be at least 3 characters long.", { autoClose: 3000 });
        return;
      } else if (realPhone(trimmedPhone)) {
        toast.error("Please enter a valid mobile number.", { autoClose: 3000 });
        return;
      }

      setBtnLoading(true);

      // Create payload with trimmed values
      const trimmedFormData = {
        ...formData,
        name: trimmedName,
        email: trimmedEmail,
        phone: trimmedPhone,
      };

      const newStudent = await registerStudent(trimmedFormData);

      if (newStudent && newStudent?.id) {
        setBtnLoading(false);
        // Primary success toast
        toast.success("🎉 Registration successful! Our team will contact you soon.", { autoClose: 4000 });

        // If resume upload failed on the server, show a warning so user knows
        if (newStudent.resumeUploadFailed) {
          toast.warn(newStudent.resumeUploadMessage || "Resume upload failed due to storage limits.", { autoClose: 6000 });
        }

        setFormData({
          name: "",
          email: "",
          phone: "",
          resume: null,
          studentLevel: STUDENT_LEVELS.FREE_TRAINEE,
        });

        // Note: setStudents function should be passed as prop or handled differently
      } else {
        setBtnLoading(false);
        toast.error(newStudent?.message || "Registration failed.", { autoClose: 3000 });
      }
    } catch (error) {
      console.error("Registration error:", error);
      const msg = error?.message || error?.code || "Registration failed. Please try again.";
      toast.error(msg, { autoClose: 5000 });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Back to Home Button */}
      <div className="max-w-7xl mx-auto mb-6">
        <button
          onClick={() => window.location.href = '/'}
          className="inline-flex items-center text-sm font-medium px-5 py-2.5 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md"
          style={{ 
            color: '#00428a',
            backgroundColor: 'white',
            border: '1px solid #e5e7eb'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f9fafb';
            e.target.style.borderColor = '#00428a';
            e.target.style.transform = 'translateX(-4px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'white';
            e.target.style.borderColor = '#e5e7eb';
            e.target.style.transform = 'translateX(0)';
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Back to Home
        </button>
      </div>
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          {/* Company Logo */}
          <div className="inline-flex items-center justify-center mb-6">
            <div className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl shadow-lg mr-4">
              <img 
                src="/assets/images/bhit-logo.png" 
                alt="Bhit Infotech Logo" 
                className="h-12 w-auto"
              />
            </div>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full">
              <Rocket className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Launch Your 
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Tech Career</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Join thousands of successful developers who started their journey with our comprehensive training program
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - Enhanced Welcome Content */}
          <div className="space-y-8">
            {/* Main Welcome Card */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mr-4">
                  <Rocket className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Welcome to Bhit Infotech
                </h2>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  🎉 Free Web Designing Classes
                </p>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Transform your passion into profession with our comprehensive web design training program.
                </p>
                <div className="inline-flex items-center bg-orange-100 text-orange-800 px-4 py-2 rounded-lg text-sm font-medium">
                  <Calendar className="w-4 h-4 mr-2" />
                  Starting soon - Don't miss out!
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center p-4 bg-green-50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">100% Free Training</span>
                </div>
                <div className="flex items-center p-4 bg-blue-50 rounded-xl">
                  <Users className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Expert Mentors</span>
                </div>
                <div className="flex items-center p-4 bg-purple-50 rounded-xl">
                  <Code className="w-5 h-5 text-purple-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Hands-on Projects</span>
                </div>
                <div className="flex items-center p-4 bg-yellow-50 rounded-xl">
                  <Award className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Certification</span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <p className="text-red-600 font-semibold text-center mb-2">
                  ⚡ Limited Seats Available!
                </p>
                <p className="text-gray-500 text-center text-sm">
                  Secure your spot in the next batch
                </p>
              </div>
            </div>

            {/* Course Highlights */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Star className="w-5 h-5 text-yellow-500 mr-2" />
                What You'll Learn
              </h3>
              <div className="space-y-3">
                <div className="flex items-center p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg">
                  <Code className="w-5 h-5 text-orange-500 mr-3" />
                  <span className="text-gray-700">HTML5 & CSS3 Fundamentals</span>
                </div>
                <div className="flex items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                  <Palette className="w-5 h-5 text-blue-500 mr-3" />
                  <span className="text-gray-700">Modern Design Frameworks</span>
                </div>
                <div className="flex items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                  <Monitor className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Responsive Web Design</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Enhanced Form */}
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            {/* Form Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6">
              <div className="flex flex-col items-center">
                {/* Bhit Logo */}
                <div className="mb-4 bg-white/10 backdrop-blur-sm p-3 rounded-xl">
                  <img 
                    src="/assets/images/bhit-logo.png" 
                    alt="Bhit Infotech Logo" 
                    className="h-10 w-auto filter brightness-0 invert"
                  />
                </div>
                <h3 className="text-2xl font-bold text-white text-center flex items-center justify-center">
                  <User className="w-6 h-6 mr-2" />
                  Registration Form
                </h3>
                <p className="text-blue-100 text-center mt-2">Fill in your details to get started</p>
              </div>
            </div>

            <div className="p-8">

              <Form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Form.Control
                    type="text"
                    required
                    value={formData.name}
                    placeholder="Enter your full name"
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value.trimStart() })
                    }
                    onBlur={(e) =>
                      setFormData({ ...formData, name: e.target.value.trim() })
                    }
                    className="border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 text-gray-800"
                  />
                </div>

                {/* Email */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <Form.Control
                    type="email"
                    required
                    value={formData.email}
                    placeholder="Enter your email address"
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value.trim() })
                    }
                    className="border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 text-gray-800"
                  />
                </div>

                {/* Phone */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <Form.Control
                    type="tel"
                    required
                    value={formData.phone}
                    placeholder="Enter your 10-digit mobile number"
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value.trim() })
                    }
                    className="border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 text-gray-800"
                  />
                </div>

                {/* Resume Upload with Drag & Drop */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Upload className="w-4 h-4 inline mr-2" />
                    Upload Resume <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ${
                      dragActive
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-2">
                      {formData.resume ? (
                        <span className="text-green-600 font-medium">
                          ✓ {formData.resume.name}
                        </span>
                      ) : (
                        <>
                          Drag and drop your resume here, or{' '}
                          <label className="text-blue-600 hover:text-blue-700 cursor-pointer font-medium">
                            browse files
                            <Form.Control
                              type="file"
                              accept=".pdf,.doc,.docx"
                              required
                              onChange={handleFileChange}
                              className="hidden"
                            />
                          </label>
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 5MB</p>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={btnLoading}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-6 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                >
                  {btnLoading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        className="text-white mr-3"
                      />
                      <span>Registering...</span>
                    </>
                  ) : (
                    <>
                      <Rocket className="w-5 h-5 mr-2" />
                      Start Your Journey
                    </>
                  )}
                </button>

                {/* Security Note */}
                <p className="text-center text-xs text-gray-500 mt-4">
                  🔒 Your information is secure and will never be shared with third parties
                </p>
              </Form>
            </div>
          </div>
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Ready to Transform Your Career?
          </h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Join our community of successful developers and start building amazing web experiences today!
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
            <div className="flex items-center">
              <Users className="w-4 h-4 mr-2" />
              <span>500+ Students Trained</span>
            </div>
            <div className="flex items-center">
              <Award className="w-4 h-4 mr-2" />
              <span>Industry Certified</span>
            </div>
            <div className="flex items-center">
              <Star className="w-4 h-4 mr-2" />
              <span>4.9/5 Rating</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Registration;
